import { createFileRoute } from '@tanstack/react-router';
import { PageShell } from '~/components/layout';
import {
  CtaBanner,
  FaqAccordion,
  FeatureBlock,
  Hero,
  StepsSection,
  TextSection,
  TravelerPartnerLogos,
  TravelerTierTags,
} from '~/components/sections';
import {
  travelerBookingSteps,
  travelerConfidence,
  travelerConfidenceTags,
  travelerDiscoverSteps,
  travelerFaq,
  travelerFaqFootnote,
  travelerHero,
  travelerIntro,
  travelerPerks,
  travelerPerksTags,
  travelerStats,
} from '~/content/traveler';
import { faqPageJsonLd, pageHeadMeta } from '~/lib/seo';
import heroCss from '~/styles/hero.css?inline';

export const Route = createFileRoute('/cards_/travelers')({
  head: () => {
    const head = pageHeadMeta({
      title: 'The nsave Card for Travel',
      description:
        'Pay in local currencies across 170+ countries wherever Visa is accepted, with travel protection, airport lounge access and exclusive hotel privileges on Visa Infinite.',
      path: '/cards/travelers',
      inlineStyles: [heroCss],
    });
    return { ...head, scripts: [...head.scripts, faqPageJsonLd(travelerFaq)] };
  },
  component: TravelerPage,
});

// Section headings in this Figma use H3 on mobile and H2 on desktop.
const headingType = '!text-h3 md:!text-h2';

// Booking to Boarding and Discover More Abroad share the larger card layout:
// 261px cards (17/15px copy) on mobile, 24/17px copy and a 455px heading rule on desktop.
const stepsClassNames = {
  heading: 'md:max-w-[455px]',
  // The mobile slider scrolls on x, which also clips y: keep the trimmed last
  // line's descenders inside it.
  track: '!mt-10 max-md:pb-1',
  card: 'max-md:!w-[261px] md:!gap-10',
  title: '!text-body !font-semibold md:!text-h4',
  description: 'md:!text-body',
};

function TravelerPage() {
  return (
    <PageShell>
      <Hero
        content={travelerHero}
        stats={travelerStats}
        // Figma: 36px headline on mobile, 56px on desktop.
        titleClassName="max-md:!text-[36px] max-md:!leading-[1.16] max-md:!tracking-[-0.022em] md:!text-[56px] md:!leading-[1.16] md:!tracking-[-0.022em]"
        ctaClassName="!mt-10 md:!mt-12"
        mediaClassName="max-md:!h-[560px]"
      />
      <TextSection
        content={travelerIntro}
        titleClassName={headingType}
        logos={<TravelerPartnerLogos />}
        sectionClassName="!mt-10 !mb-10 md:!my-20"
      />
      <StepsSection
        heading="Booking to Boarding"
        steps={travelerBookingSteps}
        classNames={{ ...stepsClassNames, section: '!py-0' }}
      />
      <FeatureBlock
        content={travelerConfidence}
        imagePosition="left"
        titleClassName={headingType}
        titleSlot={<TravelerTierTags tags={travelerConfidenceTags} />}
        sectionClassName="!pt-[120px] !pb-[50px] lg:!pt-[187px] lg:!pb-2.5"
      />
      <FeatureBlock
        content={travelerPerks}
        imagePosition="right"
        titleClassName={headingType}
        titleSlot={<TravelerTierTags tags={travelerPerksTags} />}
        sectionClassName="!pt-[50px] !pb-0 lg:!pt-2.5"
      />
      <StepsSection
        heading="Discover More Abroad"
        steps={travelerDiscoverSteps}
        classNames={{ ...stepsClassNames, section: '!pt-[120px] !pb-0 lg:!pt-[160px]' }}
      />
      <FaqAccordion
        content={travelerFaq}
        gridAligned
        toggleIcon="chevron"
        footnote={travelerFaqFootnote}
        // Figma breaks the title after "asked", which `text-balance` would move.
        headingClassName="border-b border-black-6 pb-8 lg:max-w-[455px] [&>h2]:text-wrap"
        questionClassName="!text-body"
        sectionClassName="!pt-[120px] !pb-[90px] lg:!pt-[200px] lg:!pb-[120px]"
      />
      <CtaBanner />
    </PageShell>
  );
}
